import { IBreakdownFigures } from '../services/archived-report-period/archived-report-period.model'

/** A breakdown as the form holds it: a typed figure, or '' for an entry left blank. */
export type BreakdownInputs = Record<string, number | string>

/** Which scope a breakdown splits: Scope 1 by group, Scope 3 by category. */
export type BreakdownScope = '1' | '3'

// Half the smallest step the form takes (0.01), matching the server's check, so figures that add up
// exactly never trip it on floating-point noise.
const TOLERANCE = 0.005

const isBlank = (value: unknown) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '')

/** The sum of the figures entered, ignoring blanks. */
export const sumBreakdown = (values: BreakdownInputs | IBreakdownFigures | null | undefined): number =>
  Object.values(values ?? {}).reduce<number>((sum, value) => (isBlank(value) ? sum : sum + (Number(value) || 0)), 0)

/** True when at least one entry has a figure (a typed zero counts). */
export const hasBreakdown = (values: BreakdownInputs | IBreakdownFigures | null | undefined): boolean =>
  Object.values(values ?? {}).some((value) => !isBlank(value))

/**
 * The breakdown as the API stores it: numbers by key, blanks dropped, and null when nothing was
 * entered.
 */
export const cleanBreakdown = (values: BreakdownInputs | null | undefined): IBreakdownFigures | null => {
  const cleaned: IBreakdownFigures = {}
  Object.entries(values ?? {}).forEach(([key, value]) => {
    if (!isBlank(value) && Number.isFinite(Number(value))) cleaned[key] = Number(value)
  })
  return Object.keys(cleaned).length ? cleaned : null
}

const tonnes = (value: number) => value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const subject = (scope: BreakdownScope, regionLabel: string) =>
  scope === '3' ? `${regionLabel} Scope 3 categories add` : `${regionLabel} Scope 1 breakdown adds`

/**
 * The error for a breakdown that adds up to more than its scope's total for the region, or null. A
 * blank total is never an error: it takes the breakdown's sum when the period is saved.
 */
export const breakdownError = (
  values: BreakdownInputs | null | undefined,
  total: number | string | null | undefined,
  scope: BreakdownScope,
  regionLabel: string,
): string | null => {
  if (isBlank(total) || !Number.isFinite(Number(total))) return null
  const sum = sumBreakdown(values)
  if (sum > Number(total) + TOLERANCE) {
    return `${subject(scope, regionLabel)} up to ${tonnes(sum)} tCO2e, more than the ${regionLabel} Scope ${scope} total of ${tonnes(Number(total))} tCO2e.`
  }
  return null
}

/**
 * One line telling the user how a breakdown sits against its scope's total for the region, or null
 * when the region has no breakdown for that scope.
 */
export const breakdownSummary = (
  values: BreakdownInputs | null | undefined,
  total: number | string | null | undefined,
  scope: BreakdownScope,
  regionLabel: string,
): string | null => {
  if (!hasBreakdown(values)) return null
  const sum = sumBreakdown(values)
  const label = `${regionLabel} Scope ${scope}`
  if (isBlank(total)) {
    return `${label}: no total entered, so it will be set to the breakdown's ${tonnes(sum)} tCO2e.`
  }
  const remainder = Number(total) - sum
  if (remainder > TOLERANCE) {
    return scope === '3'
      ? `${label}: categories add up to ${tonnes(sum)} of the ${tonnes(Number(total))} tCO2e total. The other ${tonnes(remainder)} tCO2e will show as other Scope 3 in your reports.`
      : `${label}: the breakdown adds up to ${tonnes(sum)} of the ${tonnes(Number(total))} tCO2e total. The other ${tonnes(remainder)} tCO2e is not attributed to either group.`
  }
  return `${label}: the breakdown adds up to the ${tonnes(Number(total))} tCO2e total.`
}
